import React, { useEffect, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { Switch, TablePagination, Snackbar, Alert } from "@mui/material";
import {
  getSalesPanelUsers,
  updateSalesPanelUser,
} from "./Helper";

const SalesUsersList = () => {
  const context = useOutletContext();
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [togglingId, setTogglingId] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");

  const fetchData = (pageNum, limitNum) => {
    setLoading(true);
    getSalesPanelUsers(pageNum + 1, limitNum)
      .then((response) => {
        const res = response.data;
        if (res?.success && res?.data) {
          setUsers(res.data.data || []);
          setTotal(res.data.total || 0);
        } else {
          setUsers([]);
          setTotal(0);
        }
      })
      .catch((error) => {
        console.error("Error fetching sales users:", error);
        setUsers([]);
        setTotal(0);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to fetch sales users";
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    context(["Team Manager", "Sales Users"]);
    fetchData(page, limit);
  }, []);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
    fetchData(newPage, limit);
  };

  const handleChangeRowsPerPage = (event) => {
    const newLimit = parseInt(event.target.value, 10);
    setLimit(newLimit);
    setPage(0);
    fetchData(0, newLimit);
  };

  const handleFlexpertAccessToggle = (user, checked) => {
    setTogglingId(user.id);
    updateSalesPanelUser(user.id, { hasHireFlexpertAccess: checked })
      .then((response) => {
        if (response?.data?.success) {
          setUsers((prev) =>
            prev.map((u) =>
              u.id === user.id ? { ...u, hasHireFlexpertAccess: checked } : u
            )
          );
          setSnackbarMessage(response.data.message || "Sales panel user updated successfully");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        }
      })
      .catch((error) => {
        console.error("Error updating Flexpert access:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to update Flexpert access";
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      })
      .finally(() => setTogglingId(null));
  };

  const handleProlegionAccessToggle = (user, checked) => {
    setTogglingId(user.id);
    updateSalesPanelUser(user.id, { hasProlegionAccess: checked })
      .then((response) => {
        if (response?.data?.success) {
          setUsers((prev) =>
            prev.map((u) =>
              u.id === user.id ? { ...u, hasProlegionAccess: checked } : u
            )
          );
          setSnackbarMessage(response.data.message || "Sales panel user updated successfully");
          setSnackbarSeverity("success");
          setSnackbarOpen(true);
        }
      })
      .catch((error) => {
        console.error("Error updating Prolegion access:", error);
        const errorMessage =
          error.response?.data?.message ||
          error.message ||
          "Failed to update Prolegion access";
        setSnackbarMessage(errorMessage);
        setSnackbarSeverity("error");
        setSnackbarOpen(true);
      })
      .finally(() => setTogglingId(null));
  };

  return (
    <div className="p-5 shadow-md bg-white">
      <div className="flex w-full justify-between">
        <div className="flex-none">
          <h3
            className="col-span-5 text-lg uppercase tracking-wider font-bold inline"
            style={{ color: "#2EBAA3" }}
          >
            Sales Users
          </h3>
        </div>
      </div>
      <br />
      <div className="overflow-x-auto relative">
        <table className="w-full text-sm text-left text-gray-500">
          <thead className="text-xs text-gray-700 uppercase bg-gray-50">
            <tr>
              <th scope="col" className="py-3 px-6 uppercase">
                Sr No.
              </th>
              <th scope="col" className="py-3 px-6 uppercase">
                Name
              </th>
              <th scope="col" className="py-3 px-6 uppercase">
                Email
              </th>
              <th scope="col" className="py-3 px-6 uppercase">
                Phone No.
              </th>
              <th scope="col" className="py-3 px-6 uppercase">
                Is Active
              </th>
              <th scope="col" className="py-3 px-6 uppercase">
                Flexpert Access
              </th>
              <th scope="col" className="py-3 px-6 uppercase">
                Prolegion Access
              </th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7" className="py-4 px-6 text-center text-gray-500">
                  Loading...
                </td>
              </tr>
            ) : users?.length > 0 ? (
              users.map((user, index) => (
                <tr
                  className="bg-white border-b hover:bg-gray-50"
                  key={user.id}
                >
                  <th
                    scope="row"
                    className="py-4 px-6 font-medium text-gray-900 whitespace-nowrap"
                  >
                    {page * limit + index + 1}
                  </th>
                  <td className="py-4 px-6">{user.name || "-"}</td>
                  <td className="py-4 px-6">{user.email || "-"}</td>
                  <td className="py-4 px-6">
                    {user.phoneNumber || "-"}
                  </td>
                  <td className="py-4 px-6">
                    {user.isActive === true ? "true" : "false"}
                  </td>
                  <td className="py-4 px-6">
                    <Switch
                      checked={!!user.hasHireFlexpertAccess}
                      disabled={togglingId === user.id}
                      onChange={(e) =>
                        handleFlexpertAccessToggle(user, e.target.checked)
                      }
                      color="primary"
                    />
                  </td>
                  <td className="py-4 px-6">
                    <Switch
                      checked={!!user.hasProlegionAccess}
                      disabled={togglingId === user.id}
                      onChange={(e) =>
                        handleProlegionAccessToggle(user, e.target.checked)
                      }
                      color="primary"
                    />
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="7"
                  className="py-4 px-6 text-center text-gray-500"
                >
                  No sales users found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      <TablePagination
        component="div"
        count={total}
        page={page}
        onPageChange={handleChangePage}
        rowsPerPage={limit}
        onRowsPerPageChange={handleChangeRowsPerPage}
        rowsPerPageOptions={[5, 10, 25, 50, 100]}
      />
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: "100%" }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default SalesUsersList;
